<template>
  <v-card>
    <v-card-title class="text-primary"> Contexte Codes d'erreur </v-card-title>

    <v-card-text>
      <!-- Product Make -->
      <v-select v-model="context.productMake" :items="productMakes" label="Marque de Produit" :rules="[(v) => !!v || 'Required']" />

      <!-- Product Type -->
      <v-select v-model="context.productType" :items="productTypes" label="Type de Produit" :rules="[(v) => !!v || 'Required']" />

      <v-divider class="my-6" />

      <!-- Error Codes -->
      <div class="d-flex justify-space-between align-center mb-2">
        <h3>Codes d'Erreur</h3>
      </div>
      <v-card v-for="(errorCode, index) in context.errorCodes" :key="index" class="mb-4 pa-4" variant="outlined">
        <div class="d-flex justify-space-between align-center mb-3">
          <strong>Code d'Erreur {{ index as number + 1 }}</strong>

          <div>
            <v-btn icon size="small" color="error" @click="removeErrorCode(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>
         <v-text-field v-model="errorCode.accuTracCode" label="Code AccuTrac" :rules="[(v) => !!v || 'Required']" />

        <v-text-field v-model="errorCode.code" label="Code" :rules="[(v) => !!v || 'Required']" />

        <v-text-field v-model="errorCode.message" label="Message" :rules="[(v) => !!v || 'Required']" />

        <v-textarea v-model="errorCode.description" label="Description" rows="2" auto-grow />

        <!-- Possible Causes -->
        <div class="d-flex align-center mb-1 mt-2">
          <span class="text-body-2 font-weight-medium">Causes possibles</span>
          <v-btn icon size="x-small" color="primary" class="ml-2" @click="addCause(errorCode)">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
        <div v-for="(_, ci) in errorCode.possibleCauses" :key="ci" class="d-flex align-center mb-1">
          <v-text-field
            v-model="errorCode.possibleCauses![ci]"
            density="compact"
            hide-details
            class="flex-grow-1"
          />
          <v-btn icon size="x-small" color="error" class="ml-1" @click="removeCause(errorCode, ci)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Recommended Actions -->
        <div class="d-flex align-center mb-1 mt-4">
          <span class="text-body-2 font-weight-medium">Actions recommandées</span>
          <v-btn icon size="x-small" color="primary" class="ml-2" @click="addAction(errorCode)">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
        <div v-for="(_, ai) in errorCode.recommendedActions" :key="ai" class="d-flex align-center mb-1">
          <v-text-field
            v-model="errorCode.recommendedActions![ai]"
            density="compact"
            hide-details
            class="flex-grow-1"
          />
          <v-btn icon size="x-small" color="error" class="ml-1" @click="removeAction(errorCode, ai)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-card-text>
    <div class="d-flex justify-center">
      <v-btn color="primary" @click="addErrorCode"> Ajouter un code d'erreur </v-btn>
      <v-btn color="white" @click="router.push({ name: 'productdiagnosticcontexts' })"> Retour </v-btn>
    </div>

    <v-card-actions class="justify-end">
      <v-btn color="primary" :loading="loading" @click="submit"> Sauvegarder </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store";
import { ProductDiagnosticContext, ProductType, ProductMake } from "@/types";
import { fetchProductDiagnosticContextById, postProductDiagnosticContext, putProductDiagnosticContext } from "@/utils/product-diagnostic-context-api";
import { computed, onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const title = ref("");
const loading = ref(false);
const context = ref<ProductDiagnosticContext>({
  productMake: null as any,
  productType: null as any,
  errorCodes: [],
});
const productTypes = Object.values(ProductType);
const productMakes = Object.values(ProductMake);

const isCreatingNewContext = computed(
  () => route.params.id === "new"
);

function addErrorCode() {
  context.value.errorCodes.push({
    code: "",
    accuTracCode: "",
    message: "",
    possibleCauses: [],
    recommendedActions: [],
  });
}

function removeErrorCode(index: number) {
  context.value.errorCodes.splice(index, 1);
}

function addCause(errorCode: (typeof context.value.errorCodes)[number]) {
  errorCode.possibleCauses = [...(errorCode.possibleCauses ?? []), ""];
}

function removeCause(errorCode: (typeof context.value.errorCodes)[number], index: number) {
  errorCode.possibleCauses = errorCode.possibleCauses?.filter((_, i) => i !== index) ?? [];
}

function addAction(errorCode: (typeof context.value.errorCodes)[number]) {
  errorCode.recommendedActions = [...(errorCode.recommendedActions ?? []), ""];
}

function removeAction(errorCode: (typeof context.value.errorCodes)[number], index: number) {
  errorCode.recommendedActions = errorCode.recommendedActions?.filter((_, i) => i !== index) ?? [];
}

async function submit() {
  try {
    loading.value = true;
    const contextData = { ...context.value };

    if (!contextData.id) {
      context.value = await postProductDiagnosticContext(contextData);

      appStore.sendSuccessNotice("Nouveau contexte de diagnostic créé.");
      router.replace({
        name: "ProductDiagnosticContextEditor",
        params: { id: context.value.id },
      });
    } else {
      await putProductDiagnosticContext(contextData);
      appStore.sendSuccessNotice("Contexte de diagnostic mis à jour.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  try {
    loading.value = true;
    if (isCreatingNewContext.value) {
      title.value = "Nouveau Contexte de Diagnostic";
    } else {
      title.value = "Éditer Contexte de Diagnostic";
      const contextId = route.params.id as string;
      context.value = await fetchProductDiagnosticContextById(contextId);
    }
  } catch (error) {
  } finally {
    loading.value = false;
  }
})
</script>
